// src/views/admin/ADImportUtilisateurs.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";

// ─── COMPOSANTS ──────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, maxWidth = 560 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Card({ children, title, icon }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      {title && (
        <div className="ch">
          <div className="ct">
            {icon && <i className={`ti ${icon}`} style={{ marginRight: 8 }} />}
            {title}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

// ─── CONSTANTES ──────────────────────────────────────────────────────────────
const FIELD_MAPPING = {
  username: { label: "Nom d'utilisateur", required: true },
  telephone1: { label: "Téléphone 1", required: false },
  telephone2: { label: "Téléphone 2", required: false },
  email: { label: "Email", required: false },
};

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────
export default function ADImportUtilisateurs({ slug }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [mapping, setMapping] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);

  const toast = useToastHook();
  const { importUtilisateurs } = useAdminService();

  // ─── GESTION FICHIER ──────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validExtensions = [".xlsx", ".xls", ".csv"];
    const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf(".")).toLowerCase();
    
    if (!validExtensions.includes(ext)) {
      toast.error("Veuillez sélectionner un fichier Excel (.xlsx, .xls) ou CSV");
      return;
    }

    setFile(selectedFile);
    readExcelFile(selectedFile);
  };

  const readExcelFile = (file) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { 
          header: 1, 
          defval: "",
          raw: false 
        });
        
        if (jsonData.length < 2) {
          toast.error("Le fichier est vide ou ne contient pas d'en-têtes");
          return;
        }

        const headers = jsonData[0].map((h, i) => {
          if (h === undefined || h === null || h === "") {
            return `Colonne ${i + 1}`;
          }
          return String(h).trim();
        });
        setExcelHeaders(headers);
        
        const autoMapping = detectAutoMapping(headers);
        setMapping(autoMapping);
        
        const preview = convertToPreview(jsonData.slice(1), headers, autoMapping);
        setPreviewData(preview);
        validateData(preview);
      } catch (error) {
        toast.error("Impossible de lire le fichier Excel");
      }
    };
    
    reader.readAsArrayBuffer(file);
  };

  const detectAutoMapping = (headers) => {
    const map = {};
    
    headers.forEach((header, index) => {
      if (!header || header === `Colonne ${index + 1}`) return;
      
      const headerLower = header.toLowerCase().trim();
      
      if (headerLower === "username" || headerLower === "nom d'utilisateur" || headerLower.includes("username") || headerLower.includes("utilisateur")) {
        map.username = index;
      }
      else if (headerLower === "telephone1" || headerLower === "téléphone 1" || headerLower === "telephone principal" || headerLower.includes("tel1") || headerLower.includes("phone1")) {
        map.telephone1 = index;
      }
      else if (headerLower === "telephone2" || headerLower === "téléphone 2" || headerLower === "telephone secondaire" || headerLower.includes("tel2") || headerLower.includes("phone2")) {
        map.telephone2 = index;
      }
      else if (headerLower === "email" || headerLower === "e-mail" || headerLower.includes("mail")) {
        map.email = index;
      }
    });

    // Mapping par position par défaut
    if (map.username === undefined && headers.length >= 1) map.username = 0;
    if (map.telephone1 === undefined && headers.length >= 2) map.telephone1 = 1;
    if (map.telephone2 === undefined && headers.length >= 3) map.telephone2 = 2;
    if (map.email === undefined && headers.length >= 4) map.email = 3;

    return map;
  };

  const convertToPreview = (rows, headers, mapping) => {
    const previewRows = [];
    
    rows.forEach((row, rowIndex) => {
      const hasData = row.some(cell => cell !== undefined && cell !== null && cell !== "");
      if (!hasData) return;
      
      const getCellValue = (index) => {
        if (index === undefined || index === null) return "";
        if (row[index] === undefined || row[index] === null) return "";
        return String(row[index]).trim();
      };
      
      previewRows.push({
        username: getCellValue(mapping.username),
        telephone1: getCellValue(mapping.telephone1),
        telephone2: getCellValue(mapping.telephone2),
        email: getCellValue(mapping.email),
        rowIndex: rowIndex + 2,
      });
    });
    
    return previewRows;
  };

  const validateData = (data) => {
    const errors = [];
    
    data.forEach((row) => {
      if (!row.username) {
        errors.push(`Ligne ${row.rowIndex}: Nom d'utilisateur requis`);
      }
      
      // Valider le téléphone 1 (optionnel mais format si présent)
      if (row.telephone1 && row.telephone1.trim() !== "") {
        const phone = row.telephone1.trim();
        if (!/^[0-9\s\+\-\(\)]{8,20}$/.test(phone)) {
          errors.push(`Ligne ${row.rowIndex}: Format de téléphone invalide "${phone}"`);
        }
      }
      
      // Valider le téléphone 2 (optionnel mais format si présent)
      if (row.telephone2 && row.telephone2.trim() !== "") {
        const phone = row.telephone2.trim();
        if (!/^[0-9\s\+\-\(\)]{8,20}$/.test(phone)) {
          errors.push(`Ligne ${row.rowIndex}: Format de téléphone secondaire invalide "${phone}"`);
        }
      }
      
      // Valider l'email (optionnel mais format si présent)
      if (row.email && row.email.trim() !== "") {
        const email = row.email.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          errors.push(`Ligne ${row.rowIndex}: Format d'email invalide "${email}"`);
        }
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // ─── DOWNLOAD TEMPLATE ────────────────────────────────────────────────────
  const downloadTemplate = () => {
    const headers = Object.keys(FIELD_MAPPING);
    const exampleData = [
      ["parent_ouedraogo", "70 23 45 67", "71 98 76 54", "parent1@email.com"],
      ["parent_traore", "76 11 22 33", "", "parent2@email.com"],
      ["parent_kabore", "79 12 34 56", "70 55 66 77", ""],
      ["parent_sawadogo", "76 99 88 77", "", "parent4@email.com"],
      ["parent_sanou", "70 12 34 56", "71 23 45 67", "parent5@email.com"],
    ];

    const instructions = [
      [],
      ["INSTRUCTIONS :"],
      ["1. Les colonnes doivent être exactement dans cet ordre"],
      ["2. Ne pas modifier les noms des colonnes"],
      ["3. username : nom d'utilisateur unique (obligatoire)"],
      ["4. telephone1 : téléphone principal (optionnel)"],
      ["5. telephone2 : téléphone secondaire (optionnel)"],
      ["6. email : adresse email (optionnel)"],
      ["7. Les mots de passe seront générés automatiquement (parent123)"],
      ["8. Tous les utilisateurs importés auront le rôle 'PARENT'"],
      ["9. Supprimez les lignes d'exemple avant d'ajouter vos données"],
    ];

    const data = [
      headers,
      ...exampleData,
      ...instructions
    ];

    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template Parents");

    const wscols = headers.map(() => ({ wch: 25 }));
    ws["!cols"] = wscols;

    const fileName = `Template_Import_Parents.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success("Template téléchargé avec succès");
  };

  // ─── IMPORT ────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!file) {
      toast.error("Veuillez sélectionner un fichier Excel");
      return;
    }

    if (!validateData(previewData)) {
      toast.error("Des erreurs de validation doivent être corrigées");
      return;
    }

    setUploading(true);
    try {
      const result = await importUtilisateurs(slug, file);
      setImportResult(result);
      setShowResultModal(true);
      
      if (result.nb_erreurs === 0) {
        toast.success(`${result.nb_parents_crees} parents importés avec succès`);
      } else {
        toast.warning(`${result.nb_parents_crees} importés, ${result.nb_erreurs} erreurs`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Erreur lors de l'import");
    } finally {
      setUploading(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setExcelHeaders([]);
    setMapping({});
    setImportResult(null);
  };

  // ─── RENDU ──────────────────────────────────────────────────────────────────
  if (loading) return <Loader />;

  return (
    <div>
      {/* En-tête avec bouton retour */}
      <div className="sb2" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button 
            className="bsec" 
            onClick={() => navigate(`/${slug}/admin/utilisateurs`)}
          >
            <i className="ti ti-arrow-left" /> Retour
          </button>
          <h2 style={{ margin: 0, color: "var(--pr)" }}>
            <i className="ti ti-file-spreadsheet" style={{ marginRight: 8 }} />
            Import des parents depuis Excel
          </h2>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="bsec" onClick={downloadTemplate}>
            <i className="ti ti-download" /> Télécharger le template
          </button>
        </div>
      </div>

      {/* Informations */}
      <Card title="Informations" icon="ti-info-circle">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, fontSize: 14 }}>
          <div>
            <span style={{ color: "var(--m)" }}>Rôle :</span>
            <span style={{ fontWeight: 600, marginLeft: 6, color: "var(--t)" }}>PARENT (fixe)</span>
          </div>
          <div>
            <span style={{ color: "var(--m)" }}>Mot de passe :</span>
            <span style={{ fontWeight: 600, marginLeft: 6, color: "var(--t)" }}>parent123 (généré automatiquement)</span>
          </div>
          <div>
            <span style={{ color: "var(--m)" }}>Téléphones/Email :</span>
            <span style={{ fontWeight: 600, marginLeft: 6, color: "var(--t)" }}>Optionnels</span>
          </div>
        </div>
        <div style={{ 
          marginTop: 12, 
          padding: 10, 
          background: "var(--am-bg)", 
          borderRadius: 6,
          fontSize: 13,
          color: "var(--am)"
        }}>
          <i className="ti ti-info-circle" /> 
          Il est recommandé de demander aux parents de changer leur mot de passe après la première connexion.
        </div>
      </Card>

      {/* Zone d'upload */}
      <Card title="Importer le fichier Excel" icon="ti-upload">
        <div 
          className="upload-area"
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = "var(--pr)";
            e.currentTarget.style.background = "var(--pr-bg)";
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = "var(--b)";
            e.currentTarget.style.background = "transparent";
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = "var(--b)";
            e.currentTarget.style.background = "transparent";
            if (e.dataTransfer.files.length) {
              const droppedFile = e.dataTransfer.files[0];
              setFile(droppedFile);
              readExcelFile(droppedFile);
            }
          }}
        >
          <div style={{ fontSize: 48, color: "var(--pr)", marginBottom: 12 }}>
            <i className="ti ti-file-spreadsheet" />
          </div>
          <h4 style={{ color: "var(--t)", marginBottom: 8 }}>
            {file ? file.name : "Déposer votre fichier Excel ici"}
          </h4>
          <p style={{ color: "var(--m)", marginBottom: 16, fontSize: 14 }}>
            ou cliquez pour sélectionner un fichier (.xlsx, .xls, .csv)
          </p>
          <input
            id="file-upload"
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <label htmlFor="file-upload" className="bprim" style={{ cursor: "pointer" }}>
            <i className="ti ti-folder-open" /> Parcourir
          </label>
          {file && (
            <div style={{ marginTop: 12, fontSize: 13, color: "var(--m)" }}>
              <i className="ti ti-file" /> {file.name} ({(file.size / 1024).toFixed(0)} Ko)
            </div>
          )}
        </div>
      </Card>

      {/* Mapping et Aperçu */}
      {file && previewData.length > 0 && (
        <>
          <Card title="Correspondance des colonnes" icon="ti-list">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {excelHeaders.map((header, index) => (
                <span key={index} className="pill neu" style={{ fontSize: 12 }}>
                  {index + 1}. {header}
                </span>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 14 }}>
              {Object.entries(FIELD_MAPPING).map(([key, field]) => {
                const colIndex = mapping[key];
                const colName = colIndex !== undefined && excelHeaders[colIndex] 
                  ? excelHeaders[colIndex] 
                  : "Non mappé";
                return (
                  <div key={key} style={{ display: "flex", gap: 8 }}>
                    <span style={{ color: "var(--m)", minWidth: 100 }}>
                      {field.label} {field.required && <span style={{ color: "var(--rd)" }}>*</span>}:
                    </span>
                    <span style={{ fontWeight: 500 }}>
                      {colIndex !== undefined ? `Colonne ${colIndex + 1} (${colName})` : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Erreurs de validation */}
          {validationErrors.length > 0 && (
            <div className="alert-banner" style={{ borderColor: "var(--rd)", background: "var(--rd-bg)", color: "var(--rd)" }}>
              <i className="ti ti-alert-circle" />
              <span>
                <strong>{validationErrors.length} erreur(s) de validation :</strong>
                <div style={{ maxHeight: 150, overflowY: "auto", marginTop: 6, fontSize: 13 }}>
                  {validationErrors.slice(0, 30).map((err, i) => (
                    <div key={i}>• {err}</div>
                  ))}
                  {validationErrors.length > 30 && (
                    <div>... et {validationErrors.length - 30} autres erreurs</div>
                  )}
                </div>
              </span>
            </div>
          )}

          {/* Aperçu des données */}
          <Card title={`Aperçu des données (${previewData.length} lignes)`} icon="ti-eye">
            <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
              <table className="tbl">
                <thead>
                  <tr style={{ position: "sticky", top: 0, background: "var(--s)", zIndex: 10 }}>
                    <th>#</th>
                    <th>Username</th>
                    <th>Téléphone 1</th>
                    <th>Téléphone 2</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 50).map((row, i) => {
                    const isValid = {
                      phone1: !row.telephone1 || /^[0-9\s\+\-\(\)]{8,20}$/.test(row.telephone1),
                      phone2: !row.telephone2 || /^[0-9\s\+\-\(\)]{8,20}$/.test(row.telephone2),
                      email: !row.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email),
                    };
                    return (
                      <tr key={i}>
                        <td style={{ color: "var(--m)", fontSize: 12 }}>{row.rowIndex}</td>
                        <td style={{ color: row.username ? "var(--t)" : "var(--rd)", fontWeight: row.username ? 500 : 400 }}>
                          {row.username || "—"}
                        </td>
                        <td style={{ color: row.telephone1 ? (isValid.phone1 ? "var(--t)" : "var(--rd)") : "var(--h)" }}>
                          {row.telephone1 || "—"}
                          {row.telephone1 && !isValid.phone1 && <span style={{ fontSize: 10, display: "block", color: "var(--rd)" }}>❌ Format invalide</span>}
                          {row.telephone1 && isValid.phone1 && <span style={{ fontSize: 10, display: "block", color: "var(--gn)" }}>✓</span>}
                        </td>
                        <td style={{ color: row.telephone2 ? (isValid.phone2 ? "var(--t)" : "var(--rd)") : "var(--h)" }}>
                          {row.telephone2 || "—"}
                          {row.telephone2 && !isValid.phone2 && <span style={{ fontSize: 10, display: "block", color: "var(--rd)" }}>❌ Format invalide</span>}
                          {row.telephone2 && isValid.phone2 && <span style={{ fontSize: 10, display: "block", color: "var(--gn)" }}>✓</span>}
                        </td>
                        <td style={{ color: row.email ? (isValid.email ? "var(--t)" : "var(--rd)") : "var(--h)" }}>
                          {row.email || "—"}
                          {row.email && !isValid.email && <span style={{ fontSize: 10, display: "block", color: "var(--rd)" }}>❌ Email invalide</span>}
                          {row.email && isValid.email && <span style={{ fontSize: 10, display: "block", color: "var(--gn)" }}>✓</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {previewData.length > 50 && (
                    <tr><td colSpan="5" style={{ textAlign: "center", color: "var(--h)", padding: 12 }}>
                      ... et {previewData.length - 50} autres lignes
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Boutons d'action */}
          <div className="factions" style={{ justifyContent: "center", gap: 16 }}>
            <button className="bsec" onClick={resetImport}>
              <i className="ti ti-refresh" /> Réinitialiser
            </button>
            <button 
              className="bprim" 
              onClick={handleImport}
              disabled={uploading || validationErrors.length > 0}
            >
              {uploading ? (
                <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Import en cours...</>
              ) : (
                <><i className="ti ti-upload" /> Importer les parents</>
              )}
            </button>
          </div>
        </>
      )}

      {/* Instructions */}
      <Card title="Instructions" icon="ti-info-circle">
        <ol style={{ color: "var(--m)", lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
          <li>Téléchargez le template Excel pour voir le format attendu</li>
          <li>Remplissez le template avec les données des parents</li>
          <li>Importez votre fichier Excel</li>
          <li>Vérifiez l'aperçu et corrigez les erreurs éventuelles</li>
          <li>Cliquez sur "Importer les parents"</li>
        </ol>
        <div style={{ 
          marginTop: 12, 
          padding: 12, 
          background: "var(--gn-bg)", 
          borderRadius: 6,
          fontSize: 13,
          color: "var(--gn)"
        }}>
          <i className="ti ti-info-circle" /> 
          Les parents importés auront automatiquement le mot de passe <strong>parent123</strong>.
          Il est recommandé de les informer de changer leur mot de passe après la première connexion.
        </div>
      </Card>

      {/* Modal Résultat */}
      {showResultModal && importResult && (
        <Modal title="Résultat de l'import" onClose={() => { setShowResultModal(false); resetImport(); }} maxWidth={600}>
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div className="card" style={{ padding: 16, textAlign: "center", borderColor: "var(--gn)" }}>
                <div style={{ fontSize: 32, color: "var(--gn)" }}>
                  <i className="ti ti-check" />
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, color: "var(--gn)" }}>
                  {importResult.nb_parents_crees || 0}
                </div>
                <div style={{ fontSize: 13, color: "var(--m)" }}>Importés avec succès</div>
              </div>
              <div className="card" style={{ padding: 16, textAlign: "center", borderColor: "var(--rd)" }}>
                <div style={{ fontSize: 32, color: "var(--rd)" }}>
                  <i className="ti ti-x" />
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, color: "var(--rd)" }}>
                  {importResult.nb_erreurs || 0}
                </div>
                <div style={{ fontSize: 13, color: "var(--m)" }}>Erreurs</div>
              </div>
            </div>

            {importResult.parents_crees && importResult.parents_crees.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--gn)" }}>
                  <i className="ti ti-check" /> Parents créés :
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                  {importResult.parents_crees.map((username, i) => (
                    <span key={i} className="pill ok" style={{ fontSize: 11 }}>{username}</span>
                  ))}
                </div>
              </div>
            )}

            {importResult.erreurs && importResult.erreurs.length > 0 && (
              <div style={{ maxHeight: 200, overflowY: "auto", fontSize: 13 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--rd)", marginBottom: 6 }}>
                  <i className="ti ti-x" /> Erreurs :
                </div>
                {importResult.erreurs.map((err, i) => (
                  <div key={i} style={{ 
                    padding: 6, 
                    borderBottom: "0.5px solid var(--b)",
                    color: "var(--rd)"
                  }}>
                    <strong>Username: {err.username || "N/A"}</strong> — {typeof err.errors === 'object' ? JSON.stringify(err.errors) : err.errors}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="factions">
            <button className="bprim" onClick={() => { setShowResultModal(false); resetImport(); }}>
              <i className="ti ti-check" /> Terminer
            </button>
          </div>
        </Modal>
      )}

      <style>{`
        @keyframes spin { 
          0% { transform: rotate(0deg); } 
          100% { transform: rotate(360deg); } 
        }

        .upload-area {
          border: 2px dashed var(--b);
          border-radius: 10px;
          padding: 40px 20px;
          text-align: center;
          transition: all 0.3s;
          cursor: pointer;
        }

        .upload-area:hover {
          border-color: var(--pr);
          background: var(--pr-bg);
        }

        .alert-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 18px;
          background: var(--am-bg);
          border: 2px solid var(--am);
          border-radius: 8px;
          color: var(--am);
          font-size: 14px;
          margin-bottom: 14px;
          flex-wrap: wrap;
        }

        .alert-banner i { font-size: 22px; flex-shrink: 0; }
        .alert-banner span { flex: 1; }
      `}</style>
    </div>
  );
}